import {
  asArray,
  asNumber,
  asOptionalString,
  asRecord,
  asString,
  reportDtoAnomaly,
} from './common';
import { FinancialCompleteness, getDisplayOrderNumber } from '../types/order';
import { normalizeFinancialCompleteness } from './financialCompleteness';
import {
  SUPPLIER_ORDER_ACTIVE_STATUS_VALUES,
  SUPPLIER_ORDER_STATUS_VALUES,
  type ActiveSupplierOrderStatus,
  type SupplierOrderStatus,
} from '../types/supplierOrder';
import { normalizeConversionUnit, type AllocationUnit } from '../utils/dimensionConversion';
import { getPersistedQuantityForUnit } from '../utils/commercialQuantity';

export interface SupplierOrderPartyDto {
  id: string;
  name: string;
  email?: string;
  role?: string;
  contactPerson?: string;
  contactPeople?: SupplierOrderContactDto[];
  phone?: string;
}

export interface SupplierOrderContactDto {
  name?: string;
  phone?: string;
  email?: string;
}

/**
 * Canonical list of valid landed cost categories for the frontend.
 * Single home — the union type and the validation Set are derived from this array.
 *
 * Adding a category requires, in lockstep:
 *   1. A new migration ALTERing the CHECK constraint on "SupplierOrderLandedCost"
 *      (see backend migration 071_create_supplier_order_landed_cost_items.sql).
 *   2. Adding the value here (the union below updates automatically).
 *   3. `supplierOrders.landedCostCategory.*` keys in
 *      frontend/public/locales/{en,pl}/orders.json.
 */
export const SUPPLIER_ORDER_LANDED_COST_CATEGORIES = [
  'TRANSPORT',
  'CUSTOMS_DUTY',
  'CUSTOMS_AGENCY',
  'INSURANCE',
  'PORT_HANDLING',
  'INSPECTION_CERT',
  'STORAGE_DEMURRAGE',
  'BANK_FEES',
  'OTHER',
] as const;

export type SupplierOrderLandedCostCategory = typeof SUPPLIER_ORDER_LANDED_COST_CATEGORIES[number];
export type SupplierOrderManualLandedCostCategory = Exclude<SupplierOrderLandedCostCategory, 'TRANSPORT'>;

// TRANSPORT remains a valid read category for historical supplier-order rows,
// but new freight costs are owned by the Transport workflow. Supplier-order
// forms derive their choices from the canonical read list so future categories
// cannot silently disappear from manual entry.
export const SUPPLIER_ORDER_MANUAL_LANDED_COST_CATEGORIES = SUPPLIER_ORDER_LANDED_COST_CATEGORIES
  .filter((category): category is SupplierOrderManualLandedCostCategory => category !== 'TRANSPORT');

/**
 * Validation bounds shared by every supplier-order amount/quantity input.
 * Must stay in lockstep with the backend copies in
 * backend/src/routes/supplierOrders.js AND backend/src/services/supplierOrderService.js.
 * Single home on the frontend — do not re-declare these per page.
 */
export const MAX_AMOUNT = 1000000000;
export const MAX_DIMENSION = 99999999.99;
export const MAX_SUPPLIER_ORDER_NUMBER_LENGTH = 64;
export const SUPPLIER_ORDER_NUMBER_PATTERN = /^[A-Za-z0-9/_ .-]+$/;
// Smallest accepted quantity (backend enforces gt: 0; precision={3} inputs
// cannot produce a positive value below this).
export const MIN_QUANTITY = 0.001;

/**
 * Shared antd Form.Item rules for supplier-order numeric inputs — single home
 * so the bound, the message key, and the backend-parity decisions cannot drift
 * between the pages that render the same field. Spread and extend at the call
 * site when a field is additionally required:
 *   rules={[{ ...supplierOrderAmountRule(t), required: true }]}
 */
type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

// Backend requires strictly positive quantities (isFloat gt: 0) — a min of 0
// here would let 0 pass inline validation and bounce off the server as a toast.
export const supplierOrderQuantityRule = (t: TranslateFn) => ({
  type: 'number' as const,
  min: MIN_QUANTITY,
  max: MAX_AMOUNT,
  message: t('orders:supplierOrders.form.quantityPositiveInvalid', { max: MAX_AMOUNT }),
});

export const supplierOrderAmountRule = (t: TranslateFn) => ({
  type: 'number' as const,
  min: 0,
  max: MAX_AMOUNT,
  message: t('orders:supplierOrders.form.amountInvalid', { max: MAX_AMOUNT }),
});

export const supplierOrderDimensionRule = (t: TranslateFn) => ({
  type: 'number' as const,
  min: 0,
  max: MAX_DIMENSION,
  message: t('orders:supplierOrders.form.dimensionInvalid', { max: MAX_DIMENSION }),
});

export const supplierOrderNumberRule = (t: TranslateFn) => ({
  max: MAX_SUPPLIER_ORDER_NUMBER_LENGTH,
  pattern: SUPPLIER_ORDER_NUMBER_PATTERN,
  message: t('orders:supplierOrders.form.orderNumberInvalid', {
    max: MAX_SUPPLIER_ORDER_NUMBER_LENGTH,
  }),
});

export interface SupplierOrderLandedCostItem {
  id?: string;
  category: SupplierOrderLandedCostCategory;
  amount: number;
  // Per-line currency (USD/EUR/PLN). `originalAmount` is the value entered in
  // `originalCurrency`; `amountPLN` is the PLN-canonical value used for totals.
  originalCurrency?: string;
  originalAmount?: number;
  exchangeRateUsed?: number | null;
  amountPLN?: number;
  note?: string | null;
  sortOrder?: number;
}

export interface SupplierOrderItemDto {
  id: string;
  name: string;
  quantity: number;
  quantityPcs?: number | null;
  quantityM2?: number | null;
  quantityM3?: number | null;
  unit: string;
  purchaseQuantity?: number;
  purchaseUnit?: string;
  conversionQuantity?: number;
  conversionUnit?: AllocationUnit | null;
  width?: number;
  height?: number;
  length?: number;
  thickness?: number;
  currency?: string;
  unitCost?: number;
  totalCost?: number;
  totalCostPLN?: number;
  exchangeRateToPLN?: number;
  exchangeRateToPLNSource?: string;
  exchangeRateToPLNTableNumber?: string;
  exchangeRateToPLNRequestedDate?: string;
  exchangeRateToPLNEffectiveDate?: string;
  productVariantId?: string;
  productCode?: string;
  productEan?: string;
  variantSku?: string;
  catalogProductName?: string;
  productCategoryName?: string | null;
  productCategoryColor?: string | null;
  productCategoryDimensionDisplayProfile?: string | null;
  description?: string;
  pzReceipt?: SupplierOrderPZReceiptDto;
}

/**
 * One non-cancelled PZ document that already covers (part of) a supplier
 * order line's quantity. Newest-first, mirrors `pzReceipt.pzDocumentId` /
 * `pzNumber` / `status` (the most recent entry) for legacy compatibility.
 */
export interface SupplierOrderPZReceiptDocumentDto {
  pzDocumentId: string;
  pzNumber: string;
  status: string;
  quantity: number;
}

/**
 * Quantity-based PZ receipt coverage for a supplier order line — the PZ
 * generation picker (frontend/src/pages/PZDetail.tsx, mode 'supplierOrder')
 * reads `remainingQuantity` to tell "fully received" from "partially
 * received" lines, while `eligible` alone still drives whether the line can
 * generate another PZ. `blockReasonCode: 'NO_REMAINING_QUANTITY'` replaces
 * the legacy `'PZ_EXISTS'` value; other codes are unchanged.
 */
export interface SupplierOrderPZReceiptDto {
  quantityScope?: 'SUPPLIER_ORDER';
  eligible: boolean;
  blockReasonCode?: string | null;
  blockReason?: string | null;
  quantity: number;
  coveredQuantity: number;
  remainingQuantity: number;
  hasUnitMismatch?: boolean;
  documents?: SupplierOrderPZReceiptDocumentDto[];
  // Legacy compat: most recent non-cancelled document (mirrors documents[0]).
  pzDocumentId?: string;
  pzNumber?: string;
  status?: string;
}

export interface SupplierOrderItemAllocationDto {
  id: string;
  orderProductId: string;
  orderProductName?: string;
  supplierOrderItemId: string;
  supplierOrderItemName?: string;
  productVariantId?: string;
  supplierOrderItemUnit?: string;
  customerQuantity: number;
  customerUnit: string;
  allocatedQuantity: number;
  allocatedUnit: string;
  surplusDisposition: 'STOCK' | 'CUSTOMER' | 'WASTE';
  effectiveAllocatedPurchaseCostPLN?: number;
  effectiveAllocatedLandedCostPLN?: number;
  effectiveAllocatedCostPLN?: number;
}

export interface SupplierOrderCustomerOrderDto {
  linkId?: string;
  orderId: string;
  supplierOrderId?: string;
  orderNumber: string;
  manualOrderNumber?: string;
  useManualNumbering?: boolean;
  customerId?: string;
  customerName?: string;
  supplierOrderItemId?: string;
  supplierOrderItemName?: string;
  supplierOrderItemProductVariantId?: string;
  supplierOrderItemProductId?: string;
  supplierOrderItemUnit?: string;
  allocatedQuantity?: number;
  allocatedUnit?: string;
  allocatedCostPLN?: number;
  effectiveAllocatedPurchaseCostPLN?: number;
  effectiveAllocatedLandedCostPLN?: number;
  effectiveAllocatedCostPLN?: number;
  itemAllocations?: SupplierOrderItemAllocationDto[];
  notes?: string;
}

export interface SupplierOrderStatusHistoryDto {
  id: string;
  supplierOrderId: string;
  status: SupplierOrderStatus;
  notes?: string;
  changedById?: string;
  changedByName?: string;
  changedByEmail?: string;
  changedByRole?: string;
  changedAt?: string;
}

export interface SupplierOrderDto {
  stockPurchase?: boolean;
  id: string;
  orderNumber: string;
  supplierId: string;
  factoryId?: string;
  status: SupplierOrderStatus;
  allowedTransitions?: SupplierOrderStatus[];
  currency?: string;
  totalAmount?: number;
  totalAmountPLN?: number;
  financialCompleteness?: FinancialCompleteness;
  exchangeRateToPLN?: number;
  exchangeRateToPLNSource?: string;
  exchangeRateToPLNTableNumber?: string;
  exchangeRateToPLNRequestedDate?: string;
  exchangeRateToPLNEffectiveDate?: string;
  landedCosts?: SupplierOrderLandedCostItem[];
  landedCostAmount?: number;
  landedCostAmountPLN?: number;
  landedCostAllocationMethod?: string;
  useManualNumbering?: boolean;
  manualOrderNumber?: string;
  clientOrderNumber?: string;
  producer?: string;
  factoryLocation?: string;
  deliveryLocation?: string;
  description?: string;
  supplierReference?: string;
  proformaNumber?: string;
  proformaDate?: string;
  invoiceNumber?: string;
  invoiceDate?: string;
  waybillNumber?: string;
  waybillDate?: string;
  orderDate?: string;
  expectedDelivery?: string;
  actualDelivery?: string;
  estimatedCompletionDate?: string;
  notes?: string;
  accountManagerId?: string;
  accountManager?: SupplierOrderPartyDto | null;
  createdBy?: SupplierOrderPartyDto | null;
  supplier?: SupplierOrderPartyDto;
  factory?: SupplierOrderPartyDto | null;
  customerOrderCount?: number;
  customerOrders?: SupplierOrderCustomerOrderDto[];
  statusHistory?: SupplierOrderStatusHistoryDto[];
  items?: SupplierOrderItemDto[];
  linkId?: string;
  supplierOrderItemId?: string;
  supplierOrderItemName?: string;
  supplierOrderItemProductVariantId?: string;
  supplierOrderItemProductId?: string;
  supplierOrderItemUnit?: string;
  allocatedQuantity?: number;
  allocatedUnit?: string;
  allocatedCostPLN?: number;
  effectiveAllocatedPurchaseCostPLN?: number;
  effectiveAllocatedLandedCostPLN?: number;
  effectiveAllocatedCostPLN?: number;
  itemAllocations?: SupplierOrderItemAllocationDto[];
  linkNotes?: string;
}

export interface SupplierOrderLinkDto {
  id: string;
  orderId: string;
  supplierOrderId: string;
  allocatedCostPLN?: number;
  supplierOrderItemId?: string;
  allocatedQuantity?: number;
  allocatedUnit?: string;
  itemAllocations?: SupplierOrderItemAllocationDto[];
  notes?: string;
}

export type SupplierOrderDocumentType = 'SUPPLIER_INVOICE' | 'TRANSPORT_INVOICE' | 'CUSTOMS_DOCUMENT' | 'OTHER';

export interface SupplierOrderDocumentDto {
  id: string;
  supplierOrderId: string;
  documentType: SupplierOrderDocumentType;
  number?: string;
  documentDate?: string;
  amount?: number;
  currency?: string;
  fileUrl: string;
  fileName: string;
  notes?: string;
  createdAt?: string;
}

export function getSupplierOrderDisplayNumber(
  order: Pick<SupplierOrderDto, 'orderNumber' | 'useManualNumbering' | 'manualOrderNumber'>
): string {
  return getDisplayOrderNumber(order);
}

function normalizeSupplierOrderStatus(raw: unknown, entity: string): SupplierOrderStatus {
  const status = asString(raw, 'DRAFT');
  if (SUPPLIER_ORDER_STATUS_VALUES.includes(status as SupplierOrderStatus)) {
    return status as SupplierOrderStatus;
  }

  reportDtoAnomaly(entity, 'status', raw);
  return 'DRAFT';
}

function normalizeAllowedSupplierOrderStatus(
  raw: unknown,
  entity: string,
): SupplierOrderStatus | null {
  const status = asString(raw);
  if (SUPPLIER_ORDER_ACTIVE_STATUS_VALUES.includes(status as ActiveSupplierOrderStatus)) {
    return status as SupplierOrderStatus;
  }

  reportDtoAnomaly(entity, 'status', raw);
  return null;
}

function normalizeStatusHistory(raw: unknown, entity: string): SupplierOrderStatusHistoryDto[] {
  return asArray(raw, (item, index) => {
    const record = asRecord(item);
    if (!record) {
      reportDtoAnomaly(entity, `[${index}]`, item);
    }

    return {
      id: asString(record?.id),
      supplierOrderId: asString(record?.supplierOrderId),
      status: normalizeSupplierOrderStatus(record?.status, `${entity}[${index}]`),
      notes: asOptionalString(record?.notes),
      changedById: asOptionalString(record?.changedById),
      changedByName: asOptionalString(record?.changedByName),
      changedByEmail: asOptionalString(record?.changedByEmail),
      changedByRole: asOptionalString(record?.changedByRole),
      changedAt: asOptionalString(record?.changedAt),
    };
  });
}

export function normalizeSupplierOrder(raw: unknown, entity = 'supplierOrder'): SupplierOrderDto {
  const record = asRecord(raw);

  if (!record) {
    reportDtoAnomaly(entity, 'root', raw);
  }

  return {
    stockPurchase: record?.stockPurchase === true,
    id: asString(record?.id),
    orderNumber: asString(record?.orderNumber),
    supplierId: asString(record?.supplierId),
    factoryId: asOptionalString(record?.factoryId),
    status: normalizeSupplierOrderStatus(record?.status, entity),
    allowedTransitions: asArray(record?.allowedTransitions, (item, index) => (
      normalizeAllowedSupplierOrderStatus(item, `${entity}.allowedTransitions[${index}]`)
    )).filter((status): status is SupplierOrderStatus => status !== null),
    currency: asOptionalString(record?.currency),
    totalAmount: asOptionalNumber(record?.totalAmount),
    totalAmountPLN: asOptionalNumber(record?.totalAmountPLN),
    financialCompleteness: normalizeFinancialCompleteness(
      record?.financialCompleteness,
      `${entity}.financialCompleteness`,
    ),
    exchangeRateToPLN: asOptionalNumber(record?.exchangeRateToPLN),
    exchangeRateToPLNSource: asOptionalString(record?.exchangeRateToPLNSource),
    exchangeRateToPLNTableNumber: asOptionalString(record?.exchangeRateToPLNTableNumber),
    exchangeRateToPLNRequestedDate: asOptionalString(record?.exchangeRateToPLNRequestedDate),
    exchangeRateToPLNEffectiveDate: asOptionalString(record?.exchangeRateToPLNEffectiveDate),
    landedCosts: normalizeLandedCostItems(record?.landedCosts, `${entity}.landedCosts`),
    landedCostAmount: asOptionalNumber(record?.landedCostAmount),
    landedCostAmountPLN: asOptionalNumber(record?.landedCostAmountPLN),
    landedCostAllocationMethod: asOptionalString(record?.landedCostAllocationMethod),
    useManualNumbering: record?.useManualNumbering === true,
    manualOrderNumber: asOptionalString(record?.manualOrderNumber),
    clientOrderNumber: asOptionalString(record?.clientOrderNumber),
    producer: asOptionalString(record?.producer),
    factoryLocation: asOptionalString(record?.factoryLocation),
    deliveryLocation: asOptionalString(record?.deliveryLocation),
    description: asOptionalString(record?.description),
    supplierReference: asOptionalString(record?.supplierReference),
    proformaNumber: asOptionalString(record?.proformaNumber),
    proformaDate: asOptionalString(record?.proformaDate),
    invoiceNumber: asOptionalString(record?.invoiceNumber),
    invoiceDate: asOptionalString(record?.invoiceDate),
    waybillNumber: asOptionalString(record?.waybillNumber),
    waybillDate: asOptionalString(record?.waybillDate),
    orderDate: asOptionalString(record?.orderDate),
    expectedDelivery: asOptionalString(record?.expectedDelivery),
    actualDelivery: asOptionalString(record?.actualDelivery),
    estimatedCompletionDate: asOptionalString(record?.estimatedCompletionDate),
    notes: asOptionalString(record?.notes),
    accountManagerId: asOptionalString(record?.accountManagerId),
    accountManager: normalizeNullableParty(record?.accountManager, `${entity}.accountManager`),
    createdBy: normalizeNullableParty(record?.createdBy, `${entity}.createdBy`),
    supplier: normalizeParty(record?.supplier, `${entity}.supplier`),
    factory: normalizeNullableParty(record?.factory, `${entity}.factory`),
    customerOrderCount: asOptionalNumber(record?.customerOrderCount),
    customerOrders: normalizeCustomerOrders(record?.customerOrders, `${entity}.customerOrders`),
    statusHistory: normalizeStatusHistory(record?.statusHistory, `${entity}.statusHistory`),
    items: normalizeItems(record?.items, `${entity}.items`),
    linkId: asOptionalString(record?.linkId),
    supplierOrderItemId: asOptionalString(record?.supplierOrderItemId),
    supplierOrderItemName: asOptionalString(record?.supplierOrderItemName),
    supplierOrderItemProductVariantId: asOptionalString(record?.supplierOrderItemProductVariantId),
    supplierOrderItemProductId: asOptionalString(record?.supplierOrderItemProductId),
    supplierOrderItemUnit: asOptionalString(record?.supplierOrderItemUnit),
    allocatedQuantity: asOptionalNumber(record?.allocatedQuantity),
    allocatedUnit: asOptionalString(record?.allocatedUnit),
    allocatedCostPLN: asOptionalNumber(record?.allocatedCostPLN),
    effectiveAllocatedPurchaseCostPLN: asOptionalNumber(record?.effectiveAllocatedPurchaseCostPLN),
    effectiveAllocatedLandedCostPLN: asOptionalNumber(record?.effectiveAllocatedLandedCostPLN),
    effectiveAllocatedCostPLN: asOptionalNumber(record?.effectiveAllocatedCostPLN),
    itemAllocations: normalizeItemAllocations(record?.itemAllocations, `${entity}.itemAllocations`),
    linkNotes: asOptionalString(record?.linkNotes),
  };
}

export function normalizeSupplierOrders(raw: unknown, entity = 'supplierOrders'): SupplierOrderDto[] {
  return asArray(raw, (item, index) => normalizeSupplierOrder(item, `${entity}[${index}]`));
}

export function normalizeSupplierOrderLink(raw: unknown, entity = 'supplierOrderLink'): SupplierOrderLinkDto {
  const record = asRecord(raw);

  if (!record) {
    reportDtoAnomaly(entity, 'root', raw);
  }

  return {
    id: asString(record?.id),
    orderId: asString(record?.orderId),
    supplierOrderId: asString(record?.supplierOrderId),
    allocatedCostPLN: asOptionalNumber(record?.allocatedCostPLN),
    supplierOrderItemId: asOptionalString(record?.supplierOrderItemId),
    allocatedQuantity: asOptionalNumber(record?.allocatedQuantity),
    allocatedUnit: asOptionalString(record?.allocatedUnit),
    itemAllocations: normalizeItemAllocations(record?.itemAllocations, `${entity}.itemAllocations`),
    notes: asOptionalString(record?.notes),
  };
}

export function normalizeSupplierOrderDocument(raw: unknown, entity = 'supplierOrderDocument'): SupplierOrderDocumentDto {
  const record = asRecord(raw);

  if (!record) {
    reportDtoAnomaly(entity, 'root', raw);
  }

  return {
    id: asString(record?.id),
    supplierOrderId: asString(record?.supplierOrderId),
    documentType: asSupplierOrderDocumentType(record?.documentType),
    number: asOptionalString(record?.number),
    documentDate: asOptionalString(record?.documentDate),
    amount: asOptionalNumber(record?.amount),
    currency: asOptionalString(record?.currency),
    fileUrl: asString(record?.fileUrl),
    fileName: asString(record?.fileName),
    notes: asOptionalString(record?.notes),
    createdAt: asOptionalString(record?.createdAt),
  };
}

export function normalizeSupplierOrderDocuments(raw: unknown, entity = 'supplierOrderDocuments'): SupplierOrderDocumentDto[] {
  return asArray(raw, (item, index) => normalizeSupplierOrderDocument(item, `${entity}[${index}]`));
}

function normalizeParty(raw: unknown, entity: string): SupplierOrderPartyDto | undefined {
  const record = asRecord(raw);
  if (!record) return undefined;

  return {
    id: asString(record.id),
    name: asString(record.name),
    email: asOptionalString(record.email),
    role: asOptionalString(record.role),
    contactPerson: asOptionalString(record.contactPerson),
    contactPeople: normalizeContacts(record.contactPeople, `${entity}.contactPeople`),
    phone: asOptionalString(record.phone),
  };
}

function normalizeNullableParty(raw: unknown, entity: string): SupplierOrderPartyDto | null {
  if (raw === null || raw === undefined) return null;
  return normalizeParty(raw, entity) || null;
}

function normalizeItems(raw: unknown, entity: string): SupplierOrderItemDto[] | undefined {
  if (!Array.isArray(raw)) return undefined;

  return asArray(raw, (item, index) => {
    const record = asRecord(item);
    if (!record) {
      reportDtoAnomaly(`${entity}[${index}]`, 'root', item);
    }

    const conversionUnit = normalizeConversionUnit(record?.conversionUnit);
    const persistedConversionQuantity = getPersistedQuantityForUnit(record, conversionUnit);

    return {
      id: asString(record?.id),
      name: asString(record?.name),
      quantity: asNumber(record?.quantity),
      quantityPcs: record?.quantityPcs == null ? null : asNumber(record.quantityPcs),
      quantityM2: record?.quantityM2 == null ? null : asNumber(record.quantityM2),
      quantityM3: record?.quantityM3 == null ? null : asNumber(record.quantityM3),
      unit: asString(record?.unit, 'pcs'),
      purchaseQuantity: asNumber(record?.purchaseQuantity ?? record?.quantity),
      purchaseUnit: asString(record?.purchaseUnit, asString(record?.unit, 'pcs')),
      conversionUnit,
      conversionQuantity: record?.conversionQuantity == null
        ? (persistedConversionQuantity == null ? undefined : asNumber(persistedConversionQuantity))
        : asNumber(record.conversionQuantity),
      width: asOptionalNumber(record?.width),
      height: asOptionalNumber(record?.height),
      length: asOptionalNumber(record?.length),
      thickness: asOptionalNumber(record?.thickness),
      currency: asOptionalString(record?.currency),
      unitCost: asOptionalNumber(record?.unitCost),
      totalCost: asOptionalNumber(record?.totalCost),
      totalCostPLN: asOptionalNumber(record?.totalCostPLN),
      exchangeRateToPLN: asOptionalNumber(record?.exchangeRateToPLN),
      exchangeRateToPLNSource: asOptionalString(record?.exchangeRateToPLNSource),
      exchangeRateToPLNTableNumber: asOptionalString(record?.exchangeRateToPLNTableNumber),
      exchangeRateToPLNRequestedDate: asOptionalString(record?.exchangeRateToPLNRequestedDate),
      exchangeRateToPLNEffectiveDate: asOptionalString(record?.exchangeRateToPLNEffectiveDate),
      productVariantId: asOptionalString(record?.productVariantId),
      productCode: asOptionalString(record?.productCode),
      productEan: asOptionalString(record?.productEan),
      variantSku: asOptionalString(record?.variantSku),
      catalogProductName: asOptionalString(record?.catalogProductName),
      productCategoryName: asOptionalString(record?.productCategoryName),
      productCategoryColor: asOptionalString(record?.productCategoryColor),
      productCategoryDimensionDisplayProfile: asOptionalString(record?.productCategoryDimensionDisplayProfile),
      description: asOptionalString(record?.description),
      pzReceipt: normalizePZReceipt(record?.pzReceipt),
    };
  });
}

function normalizePZReceiptDocuments(raw: unknown): SupplierOrderPZReceiptDocumentDto[] | undefined {
  if (!Array.isArray(raw)) return undefined;

  return raw.map((item) => {
    const record = asRecord(item);
    return {
      pzDocumentId: asString(record?.pzDocumentId),
      pzNumber: asString(record?.pzNumber),
      status: asString(record?.status),
      quantity: asNumber(record?.quantity),
    };
  });
}

// Quantity-based PZ receipt coverage (contract: see SupplierOrderPZReceiptDto
// above). `coveredQuantity`/`remainingQuantity` default defensively when the
// backend response omits them (older fixtures / legacy shape) so a line with
// no coverage data still reads as fully uncovered rather than fully covered.
function normalizePZReceipt(raw: unknown): SupplierOrderPZReceiptDto | undefined {
  const record = asRecord(raw);
  if (!record) return undefined;

  const quantity = asNumber(record.quantity);
  const coveredQuantity = asOptionalNumber(record.coveredQuantity) ?? 0;
  const remainingQuantity = asOptionalNumber(record.remainingQuantity)
    ?? Math.max(0, quantity - coveredQuantity);
  const documents = normalizePZReceiptDocuments(record.documents);
  const mostRecentDocument = documents?.[0];

  return {
    quantityScope: record.quantityScope === 'SUPPLIER_ORDER' ? 'SUPPLIER_ORDER' : undefined,
    eligible: Boolean(record.eligible),
    blockReasonCode: asOptionalString(record.blockReasonCode),
    blockReason: asOptionalString(record.blockReason),
    quantity,
    coveredQuantity,
    remainingQuantity,
    hasUnitMismatch: record.hasUnitMismatch === true,
    documents,
    pzDocumentId: asOptionalString(record.pzDocumentId) ?? mostRecentDocument?.pzDocumentId,
    pzNumber: asOptionalString(record.pzNumber) ?? mostRecentDocument?.pzNumber,
    status: asOptionalString(record.status) ?? mostRecentDocument?.status,
  };
}

function normalizeContacts(raw: unknown, entity: string): SupplierOrderContactDto[] | undefined {
  if (!Array.isArray(raw)) return undefined;

  return asArray(raw, (item, index) => {
    const record = asRecord(item);
    if (!record) {
      reportDtoAnomaly(`${entity}[${index}]`, 'root', item);
    }

    return {
      name: asOptionalString(record?.name),
      phone: asOptionalString(record?.phone),
      email: asOptionalString(record?.email),
    };
  });
}

function normalizeItemAllocations(raw: unknown, entity: string): SupplierOrderItemAllocationDto[] | undefined {
  if (!Array.isArray(raw)) return undefined;

  return asArray(raw, (item, index) => {
    const record = asRecord(item);
    if (!record) {
      reportDtoAnomaly(`${entity}[${index}]`, 'root', item);
    }

    const disposition = asString(record?.surplusDisposition, 'STOCK');
    return {
      id: asString(record?.id),
      orderProductId: asString(record?.orderProductId),
      orderProductName: asOptionalString(record?.orderProductName),
      supplierOrderItemId: asString(record?.supplierOrderItemId),
      supplierOrderItemName: asOptionalString(record?.supplierOrderItemName),
      productVariantId: asOptionalString(record?.productVariantId),
      supplierOrderItemUnit: asOptionalString(record?.supplierOrderItemUnit),
      customerQuantity: asNumber(record?.customerQuantity),
      customerUnit: asString(record?.customerUnit),
      allocatedQuantity: asNumber(record?.allocatedQuantity),
      allocatedUnit: asString(record?.allocatedUnit),
      surplusDisposition: disposition === 'CUSTOMER' || disposition === 'WASTE'
        ? disposition
        : 'STOCK',
      effectiveAllocatedPurchaseCostPLN: asOptionalNumber(record?.effectiveAllocatedPurchaseCostPLN),
      effectiveAllocatedLandedCostPLN: asOptionalNumber(record?.effectiveAllocatedLandedCostPLN),
      effectiveAllocatedCostPLN: asOptionalNumber(record?.effectiveAllocatedCostPLN),
    };
  });
}

function normalizeCustomerOrders(raw: unknown, entity: string): SupplierOrderCustomerOrderDto[] | undefined {
  if (!Array.isArray(raw)) return undefined;

  return asArray(raw, (item, index) => {
    const record = asRecord(item);
    if (!record) {
      reportDtoAnomaly(`${entity}[${index}]`, 'root', item);
    }

    return {
      linkId: asOptionalString(record?.linkId),
      orderId: asString(record?.orderId),
      supplierOrderId: asOptionalString(record?.supplierOrderId),
      orderNumber: asString(record?.orderNumber),
      manualOrderNumber: asOptionalString(record?.manualOrderNumber),
      useManualNumbering: record?.useManualNumbering === true,
      customerId: asOptionalString(record?.customerId),
      customerName: asOptionalString(record?.customerName),
      supplierOrderItemId: asOptionalString(record?.supplierOrderItemId),
      supplierOrderItemName: asOptionalString(record?.supplierOrderItemName),
      supplierOrderItemProductVariantId: asOptionalString(record?.supplierOrderItemProductVariantId),
      supplierOrderItemProductId: asOptionalString(record?.supplierOrderItemProductId),
      supplierOrderItemUnit: asOptionalString(record?.supplierOrderItemUnit),
      allocatedQuantity: asOptionalNumber(record?.allocatedQuantity),
      allocatedUnit: asOptionalString(record?.allocatedUnit),
      allocatedCostPLN: asOptionalNumber(record?.allocatedCostPLN),
      effectiveAllocatedPurchaseCostPLN: asOptionalNumber(record?.effectiveAllocatedPurchaseCostPLN),
      effectiveAllocatedLandedCostPLN: asOptionalNumber(record?.effectiveAllocatedLandedCostPLN),
      effectiveAllocatedCostPLN: asOptionalNumber(record?.effectiveAllocatedCostPLN),
      itemAllocations: normalizeItemAllocations(record?.itemAllocations, `${entity}[${index}].itemAllocations`),
      notes: asOptionalString(record?.notes),
    };
  });
}

function asSupplierOrderDocumentType(value: unknown): SupplierOrderDocumentType {
  const normalized = asString(value, 'SUPPLIER_INVOICE');
  if (
    normalized === 'SUPPLIER_INVOICE'
    || normalized === 'TRANSPORT_INVOICE'
    || normalized === 'CUSTOMS_DOCUMENT'
    || normalized === 'OTHER'
  ) {
    return normalized;
  }

  return 'SUPPLIER_INVOICE';
}

function asOptionalNumber(value: unknown): number | undefined {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }

  const parsed = asNumber(value, NaN);
  return Number.isFinite(parsed) ? parsed : undefined;
}

const VALID_LANDED_COST_CATEGORIES: Set<SupplierOrderLandedCostCategory> = new Set(
  SUPPLIER_ORDER_LANDED_COST_CATEGORIES
);

function asLandedCostCategory(value: unknown, entity: string): SupplierOrderLandedCostCategory {
  const str = asString(value, '');
  if (VALID_LANDED_COST_CATEGORIES.has(str as SupplierOrderLandedCostCategory)) {
    return str as SupplierOrderLandedCostCategory;
  }

  reportDtoAnomaly(entity, 'category', value);
  return 'OTHER';
}

function normalizeLandedCostItems(
  raw: unknown,
  entity: string,
): SupplierOrderLandedCostItem[] | undefined {
  if (!Array.isArray(raw)) return undefined;

  return raw.map((item, index) => {
    const record = asRecord(item);
    if (!record) {
      reportDtoAnomaly(`${entity}[${index}]`, 'root', item);
    }

    return {
      id: asOptionalString(record?.id),
      category: asLandedCostCategory(record?.category, `${entity}[${index}]`),
      amount: asOptionalNumber(record?.amount) ?? 0,
      originalCurrency: asOptionalString(record?.originalCurrency),
      originalAmount: asOptionalNumber(record?.originalAmount),
      exchangeRateUsed: asOptionalNumber(record?.exchangeRateUsed) ?? null,
      amountPLN: asOptionalNumber(record?.amountPLN),
      note: asOptionalString(record?.note) ?? null,
      sortOrder: asOptionalNumber(record?.sortOrder),
    };
  });
}
